import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';

import { PhxOptionComponent } from './option.component';
import { SelectFocusableItemDirective } from './selectFocusableItem.directive';
import { SelectFocusTrapItemDirective } from './selectFocusTrapItem.directive';
import { PhxSelectOption, PhxSelectValue } from './select.types';
import { PhxDataSource } from '../services/data-source/phx-data-source.service';

import mixinDisabled from '../../mixins/disabled';
import { PHX_SELECT_OPTION_TOKEN } from './select.token';
import { MagnifyingGlass, Cross, CircleLoading, ArrowLeft } from '../icon/icon-map';
import { PhxSelectService } from './select.service';
import { debounce, isNil } from '../../utils';
import { KeyboardKeys } from '../../helpers/KeyboardKeys.enum';
import { PhxSelectDefaultConfig } from './phx-select.common';
import { PhxBreakpointObserverService } from '../../services/phx-breakpoint-service';
import { PhxHeightPredictorFn } from './select.types';
import { PhxOptionItemComponent } from './option-item.component';

const baseSelectMixin = mixinDisabled(
  class {
    constructor(public _elementRef: ElementRef) { }
  }
);

const LOADER_DEFAULT_HEIGHT = 68;

@Component({
  selector: 'phx-selection-list',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhxSelectionListComponent),
      multi: true,
    },
    PhxSelectService,
  ],
  templateUrl: './selection-list.component.html',
  styleUrls: ['./select.component.scss'],
  host: {
    class: 'phx-selection-list',
    role: 'menu',
    '[attr.tabindex]': 'disabled ? null : phxTabIndex',
    '(keydown)': 'onDropdownKeydown($event)',
    '(focus)': 'onSelectionListFocus($event)',
    '[class.phx-selection-list-no-explicit-height]': '!phxListHeight',
  },
  inputs: ['disabled'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class PhxSelectionListComponent extends baseSelectMixin implements OnInit, OnChanges, AfterViewInit, OnDestroy, ControlValueAccessor {
  @ViewChild('dropdownSearchInput', { read: ElementRef }) dropdownSearchInput: ElementRef<HTMLInputElement>;
  @ViewChild('virtualScrollViewport') virtualScrollViewport: CdkVirtualScrollViewport;
  @ViewChild('optionsContainer', { read: ElementRef }) optionsContainer: ElementRef<HTMLElement>;
  @ContentChildren(PHX_SELECT_OPTION_TOKEN as any) listOfPhxOptions: QueryList<PhxOptionComponent>;
  @ViewChildren(SelectFocusableItemDirective) listItems: QueryList<SelectFocusableItemDirective>;
  @ViewChildren(SelectFocusTrapItemDirective) focusTrapElements: QueryList<SelectFocusTrapItemDirective>;
  @ViewChildren(PhxOptionItemComponent) optionItems: QueryList<PhxOptionItemComponent>;

  @Input() public phxOptions: PhxSelectOption[];
  @Input() public phxDataSource: PhxDataSource<PhxSelectOption[]>;
  @Input() public phxVirtualScrollEnabled: boolean = true;
  @Input() public phxTruncateSubText: boolean = true;
  @Input() public phxSearchPlaceholder: string = 'Search';
  @Input() public phxAllowClear = true;
  @Input() public phxShowSearch = false;
  @Input() public phxFocusSearchOnOpen = true;
  @Input() public phxCloseOnClear = false;
  @Input() public phxLoading = false;
  @Input() public phxMultiple: boolean = false;
  @Input() public phxResultNotFound: TemplateRef<any> | string;
  @Input() public phxShowRadio: boolean = false;
  @Input() public phxFilterPredicate: (searchTerm: string, opt: PhxSelectOption) => boolean;
  @Input() public phxOptionHeight: number;
  @Input() public phxListHeight: number;
  @Input() public phxEmitOnlyIndependentSelections: boolean = false;
  @Input() public phxSearchDebounceTime: number = 300;
  @Input() public phxSelectionChangeDebounceTime: number = null;
  @Input() public phxOptimisticSelection: boolean = false;
  @Input() public phxMinBufferMultiplier = 20;
  @Input() public phxMaxBufferMultiplier = 30;
  @Input() public phxShowHeader: boolean = true;
  @Input() public phxTreeView: boolean = false;
  @Input() public phxServerSearch: boolean = false;
  @Input() public phxEnableAddNewOption: boolean = false;
  @Input() public phxSearchText: string;
  @Input() public phxNewOptionValidation: (option: string) => boolean;
  @Input() public phxHighlightSubtext: boolean = false;
  @Input() public phxDontTrapFocus: boolean = false;
  @Input() public phxTabIndex: number = 0;
  @Input() public phxFooterRightTemplate: TemplateRef<any>;
  @Input() public phxPreventSelection: boolean = false;
  @Input() public phxShowFirstOptionFocused: boolean = false;
  @Input() public phxDisableParentSelection: boolean = false;
  @Input() public phxShowOnlyIndependentSelections: boolean = false;
  @Input()
  public set phxShowSubText(value: boolean) {
    this._phxShowSubText = value;
  }

  public get phxShowSubText(): boolean {
    return this._phxShowSubText || (this.isNestedOptions && this.searched$.getValue());
  }
  @Output() public readonly phxLoadingChange = new EventEmitter<boolean>();
  @Output() public readonly phxSearchChange = new EventEmitter<string>();
  @Output() public readonly phxSelectionChange = new EventEmitter<PhxSelectOption | PhxSelectOption[] | null>();
  @Output() public readonly phxClear = new EventEmitter<void>();
  @Output() public readonly phxScrollEnd = new EventEmitter<void>();
  @Output() public readonly phxFocus = new EventEmitter<void>();
  @Output() public readonly phxBlur = new EventEmitter<void>();
  @Output() public readonly phxOptionClick = new EventEmitter<void>();
  @Output() public readonly phxAddOptionClick = new EventEmitter<PhxSelectOption>();
  @Output() public readonly phxLinkOptionClick = new EventEmitter<PhxSelectOption>();
  @Output() public readonly phxFocusTrigger = new EventEmitter<void>();

  public searchedOptions: PhxSelectOption[];
  public searched$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public searchTerm: string;
  public focused: boolean;
  public virtualScrollViewportStyle;
  public options: PhxSelectOption[] = [];
  public selectedValue: PhxSelectValue[] | PhxSelectOption[] = [];
  public activeOptions$: BehaviorSubject<PhxSelectOption[]> = new BehaviorSubject<PhxSelectOption[]>([]);
  public rootOptions$: BehaviorSubject<PhxSelectOption[]> = new BehaviorSubject<PhxSelectOption[]>([]);
  public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public levelsStack: PhxSelectOption[] = [];
  public isNestedOptions: boolean = false;
  public optionHeight: number = PhxSelectDefaultConfig.optionHeight;
  public optionListLoaderHeight: number = LOADER_DEFAULT_HEIGHT;
  public readonly CrossIcon: string = Cross;
  public readonly MagnifyingGlassIcon: string = MagnifyingGlass;
  public readonly CircleLoadingIcon: string = CircleLoading;
  public readonly ArrowLeftIcon: string = ArrowLeft;
  public onChange = (val) => { };
  public onTouched = () => { };
  public focusedIndex: number = -1;
  private _previousSearchTerm: string;
  private _totalPagesWithoutSearch: number;
  private _lastActivePageWithoutSearch: number;
  private _scrolledIndex = null;
  private _skipOptions = 0;
  private _destroy$ = new Subject<void>();
  private _activeFocusElement: number = -1;
  private _isMobileView: boolean = false;
  private _phxShowSubText: boolean = false;
  private _onChangeRef: (val: any) => void;
  /** Store the scroll offsets of the parent levels to reapply them when the nested level is closed. */
  private _parentScrollOffsets: number[] = [];

  public get isCustomScrollEnabled(): boolean {
    return this._selectService.isCustomScrollStrategyEnabled();
  }

  public get isCustomTooltipEnabled(): boolean {
    return this._selectService.isCustomTooltipFeatureFlagEnabled();
  }

  public get isTooltipOnTruncateEnabled(): boolean {
    return this._selectService.isTooltipOnTruncateFeatureFlagEnabled();
  }

  private subscribeToChanges(): void {
    combineLatest([this.activeOptions$, this.searched$])
      .pipe(takeUntil(this._destroy$))
      .subscribe(([options, searched]) => {
        this.options = options;
        this.isNestedOptions = this._selectService.isNestedOptions;
        if (this.isNestedOptions && !this.isCustomScrollEnabled) {
          this.setOptionHeight();
        }
        this.adjustViewportHeightForOptions();
        if (!this.searchTerm && !options.length) this.resetOptionListLoaderHeight();
        this.initializeFocusedIndex();
        this.cdr.detectChanges();
      });

    this.rootOptions$.pipe(takeUntil(this._destroy$)).subscribe((options) => {
      this._selectService.setOptions(options);
      this.activeOptions$.next(options);
      this.handlePreSelection(this.selectedValue);
    });

    this.loading$.pipe(takeUntil(this._destroy$)).subscribe((loading: boolean) => {
      this.phxLoadingChange.emit(loading);
      if (!loading && !this.phxDataSource && !this.phxServerSearch) this.search(this.searchTerm);
      if (!loading && !this.options.length) this.resetOptionListLoaderHeight();
      this.cdr.detectChanges();
    });
  }

  private resetOptionListLoaderHeight(): void {
    this.optionListLoaderHeight = LOADER_DEFAULT_HEIGHT;
  }

  private searchOptionLabel(option: PhxSelectOption, searchTerm: string): boolean {
    if (!option.label) return false;
    return option.label.toLocaleLowerCase().includes(searchTerm.toLowerCase());
  }

  private handlePreSelection(value: PhxSelectValue[] | PhxSelectOption[]): void {
    this.resetSelectedOptions();
    this._selectService.setPreSelectedOptions(value, this.phxMultiple, this.phxEmitOnlyIndependentSelections, this.phxOptimisticSelection);
    this.adjustViewportHeightForOptions();
  }

  private focusHostElement(): void {
    setTimeout(() => this.host.nativeElement?.focus(), 80);
  }

  private handleTabKey(event: KeyboardEvent): void {
    const items = this.focusTrapElements.toArray();
    if (!this.phxDontTrapFocus && items.length) {
      event.preventDefault();
      if (this._activeFocusElement >= items.length - 1) this._activeFocusElement = -1;
      items[++this._activeFocusElement]?.focus();
    }
  }

  private focusSpecificTrapItem(name: string = ''): void {
    const items = this.focusTrapElements.toArray();
    const itemIndex = items.findIndex((item) => item.selectFocusTrapItem === name);
    if (itemIndex !== -1) {
      items[itemIndex].focus();
      this._activeFocusElement = itemIndex;
    }
  }

  public onSelectionListFocus(event: FocusEvent): void {
    setTimeout(() => {
      if(!this.phxShowSearch) this.focusVirtualScrollViewport();
      else this.focusSearchInput();
    }, 100);
  }

  isIncludes(option: PhxSelectOption, searchTerm: string, isGroupLabel: boolean): boolean {
    return this.phxFilterPredicate ? this.phxFilterPredicate(searchTerm, option) : !isGroupLabel && this.searchOptionLabel(option, searchTerm);
  }

  private searchOptions(options: PhxSelectOption[], searchTerm: string): PhxSelectOption[] | undefined {
    if (!options?.length) return;

    let searchedItems: PhxSelectOption[] = [];
    let currentGroup: PhxSelectOption;
    for (let [, currentOption] of this._selectService.allOptionsMap) {
      const isGroupLabel: boolean = !!currentOption.groupLabel;
      const isLink: boolean = !!currentOption.isLink;
      const isDivider: boolean = currentOption.isDivider;
      if (isGroupLabel) {
        const isPreviousGroup: boolean = !!searchedItems[searchedItems.length - 1]?.groupLabel;
        if (isPreviousGroup) searchedItems.pop();
        if (currentOption.groupLabel !== currentGroup?.groupLabel) {
          searchedItems.push(currentOption);
          currentGroup = currentOption;
        }
      } else if (isLink) {
        searchedItems.push(currentOption);
      } else if (isDivider) {
        const isPreviousDivider: boolean = searchedItems[searchedItems.length - 1]?.isDivider;
        if (isPreviousDivider) searchedItems.pop();
        if (searchedItems.length !== 0) searchedItems.push(currentOption);
      } else if (this.isIncludes(currentOption, searchTerm, isGroupLabel)) searchedItems.push(currentOption);
    }
    // remove the last item, if it is group label or divider, because there are no options after it.
    if (searchedItems[searchedItems.length - 1]?.groupLabel || searchedItems[searchedItems.length - 1]?.isDivider) searchedItems.pop();
    return searchedItems;
  }

  /**
   * Figures out the height possible for showing the items in the dropdown
   * based on number of items, their height and whether the search/footer need
   * to be shown and then applies it to the virtual scroll viewport.
   */
  public adjustViewportHeightForOptions(): void {
    if (!this.phxVirtualScrollEnabled || !this.phxListHeight) return;

    if (this.isCustomScrollEnabled) {
      this.adjustViewPortHeight();
    } else {
      this.adjustViewportHeightForLegacyScroll();
    }
  }

  private adjustViewPortHeight(): void {
    const { dropdownSearchInputHeight, dropdownFooterHeight, optionHeightWithSubText, optionHeight, groupLabelHeight, dividerHeight, mobileViewPadding } =
      PhxSelectDefaultConfig;
    let maxPossibleHeight: number = this.phxListHeight;
    const items: PhxSelectOption[] = this.searched$.getValue() ? this.searchedOptions : this.options;
    if (!items?.length) return;

    // Subtract space needed for search and footer
    maxPossibleHeight -= this.phxShowSearch ? dropdownSearchInputHeight : 0;
    maxPossibleHeight -= this.isShowClear ? dropdownFooterHeight : 0;

    // Use phxOptionHeight if provided, otherwise use default values
    const baseOptionHeight = this.phxOptionHeight || optionHeight;
    const baseOptionHeightWithSubText = this.phxOptionHeight || optionHeightWithSubText;

    const minHeight = Math.min(baseOptionHeight, baseOptionHeightWithSubText, groupLabelHeight);
    maxPossibleHeight = Math.max(maxPossibleHeight, minHeight);
    let runningHeight = 0;
    let itemsThatFit = 0;
    let virtualScrollViewportHeight = 0;

    for (let i = 0; i < items.length; i++) {
      let height: number;

      if (items[i].subText && this.phxShowSubText) {
        height = this._isMobileView ? baseOptionHeightWithSubText + mobileViewPadding : baseOptionHeightWithSubText;
      } else if (items[i].groupLabel) {
        const dividerHeightForGroupLabel = i > 0 ? dividerHeight : 0;
        height = groupLabelHeight + dividerHeightForGroupLabel + (this._isMobileView ? mobileViewPadding : 0);
      } else if (this.isNestedOptions && this.searched$.getValue() && this.getAncestor(items[i].value).length > 1) {
        height = this._isMobileView ? baseOptionHeightWithSubText + mobileViewPadding : baseOptionHeightWithSubText;
      } else if (items[i].isDivider) {
        height = i > 0 ? dividerHeight : 0;
      } else {
        height = this._isMobileView ? baseOptionHeight + mobileViewPadding : baseOptionHeight;
      }

      if (runningHeight + height > maxPossibleHeight) {
        break;
      }

      runningHeight += height;
      itemsThatFit++;
    }

    // Handle edge case where no items fit
    if (itemsThatFit === 0 && items.length > 0) {
      itemsThatFit = 1;
      virtualScrollViewportHeight = this._isMobileView ? baseOptionHeight + mobileViewPadding : baseOptionHeight;
    } else {
      virtualScrollViewportHeight = runningHeight;
    }

    // Apply the calculated viewport height
    this.virtualScrollViewportStyle = this.sanitizer.bypassSecurityTrustStyle(`--viewport-height: ${virtualScrollViewportHeight}px`);
    if (virtualScrollViewportHeight > 0) this.optionListLoaderHeight = virtualScrollViewportHeight;
  }

  private adjustViewportHeightForLegacyScroll(): void {
    const { dropdownSearchInputHeight, dropdownFooterHeight } = PhxSelectDefaultConfig;
    let maxPossibleHeight: number = this.phxListHeight;
    const optionHeight: number = this.optionHeight;
    const items: PhxSelectOption[] = this.searched$.getValue() ? this.searchedOptions : this.options;
    const groupLabelsCount: number = items?.filter((item) => !!item.groupLabel)?.length || 0;
    const itemsCount: number = items?.length || 0;
    let totalHeightOfElements: number = optionHeight * itemsCount;

    if (groupLabelsCount > 1) {
      const groupLabelsWithDividerHeight: number = (groupLabelsCount - 1) * 2 * optionHeight; // subtracting 1 because there is no divider with first group label
      totalHeightOfElements = optionHeight * (itemsCount - groupLabelsCount + 1) + groupLabelsWithDividerHeight;
    }

    maxPossibleHeight -= this.phxShowSearch && dropdownSearchInputHeight;
    maxPossibleHeight -= this.isShowClear && dropdownFooterHeight;
    maxPossibleHeight = Math.floor(maxPossibleHeight / optionHeight) * optionHeight;
    maxPossibleHeight = Math.max(maxPossibleHeight, optionHeight);

    const virtualScrollViewportHeight: number = Math.min(maxPossibleHeight, totalHeightOfElements);

    this.virtualScrollViewportStyle = this.sanitizer.bypassSecurityTrustStyle(`--viewport-height: ${virtualScrollViewportHeight}px`);
    this._skipOptions = virtualScrollViewportHeight / optionHeight;

    if (virtualScrollViewportHeight > 0) this.optionListLoaderHeight = virtualScrollViewportHeight;
  }

  private resetSelectedOptions(): void {
    this._selectService.clearSelectedOption();
  }

  private transformProjectedOptions(content: PhxOptionComponent[]): PhxSelectOption[] {
    return content.map(
      (item: PhxOptionComponent): PhxSelectOption => ({
        label: item.label,
        value: item.value,
        subText: item.subText,
        groupLabel: item.groupLabel,
        isDivider: item.isDivider,
        isLink: item.isLink,
        disabled: item.disabled,
        data: item.data,
        optionTemplate: item.phxOptionTemplate,
        prefixIcon: item.prefixIcon,
        tooltip: item.tooltip,
        suffixTemplate: item.suffixTemplate,
      })
    );
  }

  public getAncestor(key: string): PhxSelectOption[] {
    return this._selectService.getAllAncestors(key);
  }

  public get templatesContext(): PhxSelectOption | PhxSelectOption[] {
    return this.phxMultiple ? this._selectService.selectedOptions : this._selectService.firstSelectedOption;
  }

  private setOptionHeight(): void {
    let optionHeight: number = PhxSelectDefaultConfig.optionHeight;

    if (this.phxOptionHeight) optionHeight = this.phxOptionHeight;
    else if (this.phxShowSubText || (this.isNestedOptions && this.searched$.getValue())) optionHeight = PhxSelectDefaultConfig.optionHeightWithSubText;

    // Add 8px for increase in 4px padding on top and bottom mobile view
    if (this._isMobileView) this.optionHeight = optionHeight + 8;
    else this.optionHeight = optionHeight;

    this.cdr.markForCheck();
  }

  public get selectedItemsCount(): number {
    return this._selectService.selectedItemsCount;
  }

  public get selectedOptions(): PhxSelectOption[] {
    return this.phxEmitOnlyIndependentSelections
      ? this._selectService.filteredSelectedOptions
      : this._selectService.selectedOptions;
  }

  public get selectedOptionsForUI(): PhxSelectOption[] {
    return this.phxShowOnlyIndependentSelections
      ? this._selectService.filteredSelectedOptions
      : this._selectService.selectedOptions;
  }

  public get triggerOptionCount(): number {
    return this.phxShowOnlyIndependentSelections
      ? this._selectService.filteredSelectedOptions.length
      : this._selectService.selectedItemsCount;
  }

  public get firstSelectedOption(): PhxSelectOption {
    return this._selectService.firstSelectedOption;
  }

  public get hasSelectedOption(): boolean {
    return !!this._selectService.selectedItemsCount;
  }

  public get selectedOptionCount(): number {
    return this._selectService.selectedItemsCount;
  }

  public get tooltipTitle(): string {
    const options = this.selectedOptionsForUI;
    const items: string[] = [];
    options?.slice(0, PhxSelectDefaultConfig.maxItemsBadgeTooltip).forEach((option: PhxSelectOption) => items.push(option.label));
    const moreCount = options.length - items.length;
    if (options?.length > PhxSelectDefaultConfig.maxItemsBadgeTooltip) items.push(`+${moreCount} more`);
    return items.join(', ');
  }

  public get isShowClear(): boolean {
    return this.phxAllowClear && !!this._selectService.selectedItemsCount;
  }

  public get isNotFoundTemplateAvailable(): boolean {
    return typeof this.phxResultNotFound !== 'string';
  }

  public get isSearchChanged(): boolean {
    return this.searchTerm !== this._previousSearchTerm;
  }

  public get headerTitle(): string {
    return this.levelsStack[this.levelsStack?.length - 1]?.label || '';
  }
  public get isRootLevel(): boolean {
    return !this.levelsStack.length;
  }

  public onScrollIndexChange(index: number) {
    if (!this.isCustomScrollEnabled) {
      this._scrolledIndex = index;
      const scrolledItems = this._scrolledIndex + this._skipOptions;
      const endItem = this.virtualScrollViewport.getRenderedRange().end;
      if (scrolledItems === endItem) {
        this.phxScrollEnd.emit();
        if (this.phxDataSource) {
          this.phxDataSource.getNextPage();
        }
      }
    } else {
      this._scrolledIndex = index;
    }
  }

  public writeValue(value: PhxSelectValue | PhxSelectOption | PhxSelectValue[] | PhxSelectOption[]): void {
    if (isNil(value)) this.selectedValue = [];
    else this.selectedValue = Array.isArray(value) ? value : ([value] as PhxSelectValue[] | PhxSelectOption[]);
    this.handlePreSelection(this.selectedValue);
    this.adjustViewportHeightForOptions();
    this.cdr.markForCheck();
  }

  public registerOnChange(fn: any): void {
    this._onChangeRef = fn;
    if (this.phxSelectionChangeDebounceTime) {
      this.onChange = debounce(fn, this.phxSelectionChangeDebounceTime);
    } else {
      this.onChange = fn;
    }
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public selectOption(option: PhxSelectOption | null): void {
    if (option) {
      this._selectService.setSelectionState(option.value, this.phxMultiple);
    } else {
      this.resetSelectedOptions();
    }

    const values: PhxSelectValue[] | PhxSelectOption[] = this.phxOptimisticSelection
      ? this.selectedOptions
      : this.phxEmitOnlyIndependentSelections
        ? this._selectService.filteredSelectedOptionKeys
        : this._selectService.selectedOptionKeys;

    this.selectedValue = values;
    this.onChange(this.phxMultiple ? values : (values.length > 0 ? values[0] : null));
    this.phxSelectionChange.emit(this.phxMultiple ? this.selectedOptions : (this.selectedOptions.length > 0 ? this.selectedOptions[0] : null));
  }

  public handleOptionClick(option: PhxSelectOption | null, index: number = -1): void {
    if (option?.disabled) return;
    if (index !== -1) {
      // Sync focusedIndex with the clicked item
      this.focusedIndex = index;
    }
    if (this.phxPreventSelection) this.onChange(this.phxOptimisticSelection ? option : option?.value)
    else this.selectOption(option);
    this.adjustViewportHeightForOptions();
    this.phxOptionClick.emit();
  }

  public onOptionHover(option: PhxSelectOption, index: number): void {
    if (option?.disabled || option?.groupLabel || option?.isDivider) return;
    if (this.focusedIndex !== index) {
      this.focusedIndex = index;
      this.cdr.markForCheck();
    }
  }

  public handleAddOptionClick(option: PhxSelectOption): void {
    this.phxAddOptionClick.emit({ label: option.label, value: option.value });
    // Added delay to prevent clearing the search term before the dropdown closes
    setTimeout(() => {
      this.searchTerm = '';
      this.search(this.searchTerm);
    }, 100);
  }

  public handleLinkOptionClick(option: PhxSelectOption): void {
    if (option.disabled) return;
    this.phxLinkOptionClick.emit(option);
  }

  public showChildren(option: PhxSelectOption): void {
    this._parentScrollOffsets.push(this.measureOptionListScrollOffset());
    this.setOptionListScrollOffset(0);
    this.activeOptions$.next(option.children);
    this.levelsStack.push(option);
    // Find the index of the first selectable option, skipping disabled options and group labels at the top.
    const indexToActive = option.children.findIndex((opt) => !opt.disabled && !opt.groupLabel);
    // Sync focusedIndex with the first selectable child
    this.focusedIndex = indexToActive >= 0 ? indexToActive : 0;
  }

  public showParent(): void {
    const parentOption = this.levelsStack.pop();
    let activeOptions: PhxSelectOption[] = [];
    if (!this.levelsStack.length) activeOptions = this.phxOptions;
    else activeOptions = this.levelsStack[this.levelsStack.length - 1].children;
    const parentScrollOffset = this._parentScrollOffsets.pop() ?? 0;
    this.setOptionListScrollOffset(parentScrollOffset);
    this.activeOptions$.next(activeOptions);
    // Find the index of the currently active children's parent.
    const indexToActive = activeOptions.findIndex((opt) => opt.value === parentOption.value);
    // Sync focusedIndex with the parent option
    this.focusedIndex = indexToActive >= 0 ? indexToActive : 0;
    this.cdr.detectChanges();
    this.reapplyOptionListScrollOffset(parentScrollOffset);
  }

  public isTreeExpanded(value: PhxSelectValue): boolean {
    return this._selectService.expandedOptionKeys.has(value);
  }

  public expandCollapseTreeOption(option: PhxSelectOption): void {
    const isExpanding = !this.isTreeExpanded(option.value);
    const currentOptionIndex = this.focusedIndex;

    const newOptions = isExpanding
      ? this._selectService.expandTreeChildren(option, this.options)
      : this._selectService.collapseTreeChildren(option, this.options);
    this.activeOptions$.next(newOptions);

    // After expanding, move focus to the first child of the expanded tree
    // After collapsing, keep focus on the current option (adjust index since children are removed)
    if (isExpanding && option.children?.length) {
      // Find the first selectable child (skip group labels and disabled items)
      const firstChildIndex = newOptions.findIndex((opt, idx) =>
        idx > currentOptionIndex && !opt.groupLabel && !opt.disabled
      );
      if (firstChildIndex !== -1) {
        this.focusedIndex = firstChildIndex;
      }
    } else if (!isExpanding) {
      // When collapsing, find the parent option in the new options array
      const parentIndex = newOptions.findIndex(opt => opt.value === option.value);
      if (parentIndex !== -1) {
        this.focusedIndex = parentIndex;
      }
    }

    this.scrollFocusedItemIntoView();
    this.cdr.markForCheck();
  }

  public removeTag(event: MouseEvent | KeyboardEvent, option: PhxSelectOption, index: number): void {
    event.stopPropagation();
    this.handleOptionClick(option, index);
  }

  public clearOption(): void {
    this.selectOption(null);
    this.adjustViewportHeightForOptions();
    this.phxClear.emit();
  }

  public onClearKeydown(): void {
    this.clearOption();
    if (this.phxCloseOnClear) this.focusHostElement();
    else this.focusSpecificTrapItem('options');
  }

  public focusSearchInput(): void {
    if (this.dropdownSearchInput) this.dropdownSearchInput.nativeElement.focus();
  }

  public focusVirtualScrollViewport(): void {
    this.virtualScrollViewport?.elementRef?.nativeElement?.focus();
  }

  public virtualScrollToIndex(): void {
    this.virtualScrollViewport?.scrollToIndex(this._scrolledIndex);
  }

  public onSearchChange(event: Event): void {
    const searchTerm: string = (event.target as HTMLInputElement).value;
    this.searchTerm = searchTerm;
    this.search(searchTerm);
  }

  public search(searchTerm: string): void {
    if (this.phxServerSearch) {
      this.phxSearchChange.emit(searchTerm);
      return;
    }

    if (!searchTerm) {
      this.onClearSearch();
      return;
    }

    if (this.phxDataSource) {
      const params = new Map<string, any>();
      params.set('search', searchTerm);
      this.phxDataSource.load(params);
    } else {
      this.searchedOptions = this.searchOptions(this.options, searchTerm);
      this.searched$.next(true);
    }

    this.cdr.detectChanges();
    this.phxSearchChange.emit(searchTerm);
  }

  public onClearSearch(): void {
    this.phxSearchChange.emit('');
    this.searchTerm = '';
    if (this.phxServerSearch) return;
    this._previousSearchTerm = '';
    this.searched$.next(false);
    this.searchedOptions = null;
    if (this.phxDataSource) {
      this.setDataSourceParamsOnClear();
      this._selectService.setOptions(this.options);
    }
    this.cdr.markForCheck();
  }

  private setDataSourceParamsOnClear(): void {
    const params = new Map<string, any>();
    params.set('page_no', this._lastActivePageWithoutSearch);
    this.phxDataSource.setParams(params);
    if (this.phxDataSource.pagination) {
      this.phxDataSource.pagination.pageNo = this._lastActivePageWithoutSearch;
      this.phxDataSource.pagination.noOfPages = this._totalPagesWithoutSearch;
    }
  }

  public isOptionSelected(option: PhxSelectOption): boolean {
    return this._selectService.isSelected(option?.value);
  }

  public isOptionIndeterminate(option: PhxSelectOption): boolean {
    return this._selectService.isIndeterminate(option?.value);
  }

  public onOptionsFocus(): void {
    // Scroll the focused item into view when options container gets focus
    setTimeout(() => this.scrollFocusedItemIntoView(), 0);
    this.onTouched();
  }

  public focusFirstItem(): void {
    const options = this.searched$.getValue() ? this.searchedOptions : this.options;
    if (!options?.length) return;
    const firstSelectableIndex = options.findIndex(opt => !opt.groupLabel && !opt.disabled);
    this.focusedIndex = firstSelectableIndex >= 0 ? firstSelectableIndex : 0;
    this.cdr.markForCheck();
  }

  public focus(): void {
    this.host.nativeElement?.focus();
  }

  public onDropdownKeydown(event: KeyboardEvent): void {
    switch (event.code) {
      case KeyboardKeys.TAB:
        this.handleTabKey(event);
        break;
    }
  }

  public onOptionsKeydown(event: KeyboardEvent): void {
    this.handleKeyboardNavigation(event, 'options');
  }

  /**
   * Initialize focusedIndex to the first selectable item (skip group labels and disabled items)
   */
  public initializeFocusedIndex(): void {
    const options = this.searched$.getValue() ? this.searchedOptions : this.options;
    if (!options?.length || !this.phxShowFirstOptionFocused) {
      this.focusedIndex = -1;
      return;
    }

    this.focusFirstItem();
  }

  /**
   * Navigate focusedIndex up or down with wrapping and skip logic
   */
  public navigateFocusedIndex(direction: 'up' | 'down'): void {
    const options = this.searched$.getValue() ? this.searchedOptions : this.options;
    if (!options?.length) return;

    const step = direction === 'down' ? 1 : -1;
    const startIndex = direction === 'up' && this.focusedIndex < 0 ? 0 : this.focusedIndex;
    let newIndex = startIndex;

    do {
      newIndex = (newIndex + step + options.length) % options.length;

      // Select if the option is selectable.
      if (!options[newIndex].groupLabel && !options[newIndex].disabled && !options[newIndex].isDivider) {
        break;
      }
    } while (newIndex !== startIndex);

    if (newIndex !== startIndex || this.focusedIndex < 0) {
      this.focusedIndex = newIndex;
      this.scrollFocusedItemIntoView();
      this.cdr.markForCheck();
    }
  }

  /**
   * Scroll the focused item into view only if it's not already visible.
   * Uses scrollIntoView with 'nearest' to minimize scrolling.
   */
  private scrollFocusedItemIntoView(): void {
    if (this.phxVirtualScrollEnabled && this.virtualScrollViewport) {
      // For virtual scroll, check if the focused item is within the rendered range
      const renderedRange = this.virtualScrollViewport.getRenderedRange();

      if (this.focusedIndex < renderedRange.start || this.focusedIndex >= renderedRange.end) {
        // Item is not rendered, need to scroll to bring it into view
        this.virtualScrollViewport.scrollToIndex(this.focusedIndex);
      } else {
        // Item is rendered, use scrollIntoView for smooth minimal scrolling
        const localIndex = this.focusedIndex - renderedRange.start;
        const items = this.optionItems?.toArray();
        if (items?.[localIndex]) {
          items[localIndex].elementRef.nativeElement.scrollIntoView({ block: 'nearest' });
        }
      }
    } else {
      // For non-virtual scroll, simply use scrollIntoView on the element
      const items = this.optionItems?.toArray();
      if (items?.[this.focusedIndex]) {
        items[this.focusedIndex].elementRef.nativeElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }

  /**
   * Either scroll mode renders its own container, so read the offset from the one that is live.
   */
  private measureOptionListScrollOffset(): number {
    if (this.phxVirtualScrollEnabled) return this.virtualScrollViewport?.measureScrollOffset() ?? 0;
    return this.optionsContainer?.nativeElement.scrollTop ?? 0;
  }

  /**
   * Nested levels share one scroll container, so the browser keeps the outgoing level's scrollTop
   * and the incoming list would render from that offset.
   * Call before activeOptions$ emits, so the synchronous re-render measures the intended offset.
   */
  private setOptionListScrollOffset(offset: number): void {
    if (this.phxVirtualScrollEnabled) this.virtualScrollViewport?.scrollToOffset(offset);
    else if (this.optionsContainer) this.optionsContainer.nativeElement.scrollTop = offset;
  }

  /**
   * The browser clamps scrollTop to the container's current scrollable range, so a deep parent
   * offset is lost whenever the child list we are leaving was shorter than that offset.
   * Without virtual scroll the container's height is the rows detectChanges just rendered, so the
   * offset is safe to re-apply synchronously. CdkVirtualScrollViewport instead publishes the
   * incoming list's spacer height through markForCheck, which lands after this task, so wait for it.
   */
  private reapplyOptionListScrollOffset(offset: number): void {
    if (!offset) return;
    if (this.phxVirtualScrollEnabled) setTimeout(() => this.setOptionListScrollOffset(offset));
    else this.setOptionListScrollOffset(offset);
  }

  /**
   * Handle keyboard events on the search input
   */
  public onSearchInputKeydown(event: KeyboardEvent): void {
    this.handleKeyboardNavigation(event, 'search');
  }

  /**
   * Unified keyboard navigation handler for both search input and options container.
   * @param event - The keyboard event
   * @param source - 'search' for search input, 'options' for options container
   */
  private handleKeyboardNavigation(event: KeyboardEvent, source: 'search' | 'options'): void {
    const options = this.searched$.getValue() ? this.searchedOptions : this.options;
    const isFromSearch = source === 'search';
    const focusedOption = options?.[this.focusedIndex];

    switch (event.code) {
      case KeyboardKeys.SPACE:
        if (isFromSearch) return;
      case KeyboardKeys.ENTER:
        event.preventDefault();
        if (focusedOption && focusedOption.isLink) {
          this.handleLinkOptionClick(focusedOption);
          return;
        }
        if (focusedOption && !focusedOption.disabled && !focusedOption.groupLabel) {
          if (this.phxDisableParentSelection && focusedOption?.children && !this.phxTreeView) {
            this.showChildren(focusedOption);
            break; // Do not focus trigger when navigating to children
          }
          this.handleOptionClick(focusedOption, this.focusedIndex);
        }
        // Focus trigger: from options always (single select), from search only if not tree view
        if (!this.phxMultiple && (!isFromSearch || !this.phxTreeView)) {
          this.phxFocusTrigger.emit();
        }
        break;

      case KeyboardKeys.ARROW_DOWN:
        event.preventDefault();
        this.navigateFocusedIndex('down');
        break;

      case KeyboardKeys.ARROW_UP:
        event.preventDefault();
        this.navigateFocusedIndex('up');
        break;

      case KeyboardKeys.ARROW_LEFT:
        if (isFromSearch) return;
        if (this.phxTreeView) {
          // For tree view, collapse the expanded option
          if (focusedOption?.children && this.isTreeExpanded(focusedOption.value)) {
            event.preventDefault();
            this.expandCollapseTreeOption(focusedOption);
          }
        } else if (this.levelsStack.length) {
          // For nested options, navigate to parent
          event.preventDefault();
          this.showParent();
          this.focusOptionsContainer();
        }
        break;

      case KeyboardKeys.ARROW_RIGHT:
        if (isFromSearch) return;
        if (focusedOption?.children) {
          event.preventDefault();
          if (this.phxTreeView) {
            // For tree view, expand the option
            this.expandCollapseTreeOption(focusedOption);
          } else {
            // For nested options, navigate into children
            this.showChildren(focusedOption);
            this.focusOptionsContainer();
          }
        }
        break;
    }
  }

  /**
   * Focus the options container (virtual scroll viewport or regular container)
   */
  private focusOptionsContainer(): void {
    setTimeout(() => {
      if (this.phxVirtualScrollEnabled) {
        this.focusVirtualScrollViewport();
      } else {
        // For non-virtual scroll, focus the host element which captures keyboard events
        this.host.nativeElement?.focus();
      }
    }, 100);
  }

  public hasNoData$: Observable<boolean> = combineLatest([this.loading$, this.searched$, this.activeOptions$]).pipe(
    map(([loading, searched, activeOptions]) => {
      return !loading && (searched ? !this.searchedOptions?.length : !activeOptions?.length);
    })
  );

  /**
   * Returns true if the new option is valid based on the search term and the validation function.
   * Following are the validation rules:
   * 1. The search term should be at least 2 characters long.
   * 2. The search term should not completely match any of the existing options.
   * 3. If a custom validation function is provided, it should return true.
   */
  public get isNewOptionValid(): boolean {
    if (this.searchTerm?.trim().length < 2) return false;
    if (this.options.some((option) => option.label && option.label.toLowerCase() === this.searchTerm?.toLowerCase())) return false;
    if (this.phxNewOptionValidation) return this.phxNewOptionValidation(this.searchTerm);
    return true;
  }

  public trackByFn(index: number, option: PhxSelectOption): string {
    if (option.value != null) return String(option.value);
    if (option.groupLabel) return `group:${option.groupLabel}`;
    if (option.isDivider) return `divider:${index}`;
    return `idx:${index}`;
  }

  public setFocusIndex(index: number): void {
    this.focusedIndex = index;
    this.cdr.markForCheck();
  }

  constructor(
    private host: ElementRef<HTMLElement>,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
    private _selectService: PhxSelectService,
    private _phxBreakpointObserver: PhxBreakpointObserverService
  ) {
    super(host);
  }

  ngOnChanges(simpleChanges: SimpleChanges): void {
    const {
      phxSelectionChangeDebounceTime,
      phxSearchDebounceTime,
      phxMultiple,
      phxDataSource,
      phxShowSearch,
      phxOptions,
      phxLoading,
      phxListHeight,
      phxOptionHeight,
      phxShowSubText,
      phxSearchText,
    } = simpleChanges;

    if (phxMultiple) {
      this.selectedValue = phxMultiple.currentValue ? [] : null;
    }

    if (phxDataSource?.currentValue) {
      phxDataSource.currentValue.data$.pipe(takeUntil(this._destroy$)).subscribe((data: PhxSelectOption[]) => {
        if (this.searchTerm) {
          if (this.isSearchChanged) {
            this.searchedOptions = data;
            this.virtualScrollViewport?.scrollToIndex(0);
          } else {
            this.searchedOptions = [...(this.searchedOptions || []), ...data];
          }
          this._selectService.setOptions(this.searchedOptions);
          this.searched$.next(true);
          this._previousSearchTerm = this.searchTerm;
        } else {
          const options = [...this.options, ...data];
          this.rootOptions$.next(options);
          this._selectService.setOptions(options);
          this._totalPagesWithoutSearch = phxDataSource.currentValue.pagination?.noOfPages;
          this._lastActivePageWithoutSearch = phxDataSource.currentValue.pagination?.pageNo;
        }
      });

      phxDataSource.currentValue.error$.pipe(takeUntil(this._destroy$)).subscribe(() => {
        if (this.searchTerm && this.isSearchChanged) {
          this.searched$.next(true);
          this.searchedOptions = [];
        }
      });

      phxDataSource.currentValue.loading$.pipe(takeUntil(this._destroy$)).subscribe((loading: boolean) => this.loading$.next(loading));
    }

    if (phxShowSearch?.currentValue) {
      this.adjustViewportHeightForOptions();
    }

    if (phxOptions?.currentValue && !phxOptions?.firstChange) {
      this.rootOptions$.next(this.phxOptions);
    }

    if (phxLoading?.currentValue !== undefined) {
      this.loading$.next(phxLoading.currentValue);
    }

    if (phxListHeight) {
      this.adjustViewportHeightForOptions();
    }

    if (phxOptionHeight) {
      this.phxOptionHeight = this.phxOptionHeight || PhxSelectDefaultConfig.optionHeight;
      this.adjustViewportHeightForOptions();
    }

    if (phxSelectionChangeDebounceTime) {
      this.onChange = debounce(this._onChangeRef, phxSelectionChangeDebounceTime.currentValue);
    }

    if (phxSearchDebounceTime) {
      this.debouncedOnSearchChange = debounce(this.onSearchChange.bind(this), phxSearchDebounceTime.currentValue);
    }

    if (phxOptionHeight || phxShowSubText) {
      if (!this.isCustomScrollEnabled) {
        this.setOptionHeight();
      }
      this.adjustViewportHeightForOptions();
    }

    if (phxSearchText) {
      this.searchTerm = this.phxSearchText || '';
      this.search(this.searchTerm);
    }
  }

  ngOnInit(): void {
    if (this.phxDataSource) this.phxDataSource.load();
    this.subscribeToChanges();
  }

  ngAfterContentInit(): void {
    if (this.listOfPhxOptions.length) {
      this.rootOptions$.next(this.transformProjectedOptions(this.listOfPhxOptions.toArray()));
    }
    this.listOfPhxOptions.changes.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.rootOptions$.next(this.transformProjectedOptions(this.listOfPhxOptions.toArray()));
    });
  }

  public debouncedOnSearchChange = debounce(this.onSearchChange.bind(this), this.phxSearchDebounceTime);

  public onInputFocus() {
    this.phxFocus.emit();
    this.onTouched();
  }

  public onInputBlur() {
    this.phxBlur.emit();
  }

  public ngAfterViewInit(): void {
    this._phxBreakpointObserver.smMobile.pipe(takeUntil(this._destroy$)).subscribe((isSmMobile: boolean) => {
      this._isMobileView = isSmMobile;
      if (!this.isCustomScrollEnabled) {
        this.setOptionHeight();
      }
    });

    if (this.phxOptions) this.rootOptions$.next(this.phxOptions);
  }

  public get isMobileView(): boolean {
    return this._isMobileView;
  }

  public onLastOptionVisible(): void {
    this.phxScrollEnd.emit();
    if (this.phxDataSource) {
      this.phxDataSource.getNextPage();
    }
  }

  public customHeightPredictor: PhxHeightPredictorFn =
    (option: PhxSelectOption, index: number) => {
      const { optionHeight, optionHeightWithSubText, groupLabelHeight, dividerHeight, mobileViewPadding } = PhxSelectDefaultConfig;

      if (this.phxOptionHeight) {
        return this._isMobileView ? this.phxOptionHeight + mobileViewPadding : this.phxOptionHeight;
      }

      if (option.subText && this.phxShowSubText) {
        return this._isMobileView ? optionHeightWithSubText + mobileViewPadding : optionHeightWithSubText;
      }

      if (option.groupLabel) {
        return groupLabelHeight + (index > 0 ? dividerHeight : 0) +
          (this._isMobileView ? mobileViewPadding : 0);
      }

      if (option.isDivider) {
        return index > 0 ? dividerHeight : 0;
      }

      if (this.isNestedOptions && this.searched$.getValue() && this.getAncestor(option.value).length > 1) {
        return this._isMobileView ? optionHeightWithSubText + mobileViewPadding : optionHeightWithSubText;
      }

      return this._isMobileView ? optionHeight + mobileViewPadding : optionHeight;
    };

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
